import { DSContent, DSContentGroup, DSDetailAndTime, DSPlayerOBj } from "./types";
import { toDate } from "date-fns";

export const getUTCDateNowWithTimezone = (timezone: string) => {
  const now = new Date();
  const utcTimestamp = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    now.getUTCHours(),
    now.getUTCMinutes(),
    now.getUTCSeconds(),
    now.getUTCMilliseconds()
  );

  const utcDate = toDate(utcTimestamp);
  return new Date(utcDate.toLocaleString("en-US", { timeZone: timezone }));
};

export const convertFromUTcToTimezone = (utcDate: string | Date, timezone: string) => {
  if (typeof utcDate === "string") {
    // If the backend returns a date without the .000Z, we add it. We know it's stored as UTC in DB.
    if (!utcDate.endsWith(".000Z")) {
      utcDate = `${utcDate}.000Z`;
    }
  }
  const date = new Date(utcDate);
  return new Date(date.toLocaleString("en-US", { timeZone: timezone }));
};

const convertContentTimezone = (content: DSContent<any>, timezone: string) => {
  if (content.startDate) content.startDate = convertFromUTcToTimezone(content.startDate, timezone);
  if (content.endDate) content.endDate = convertFromUTcToTimezone(content.endDate, timezone);
  return content;
};

const convertContentGroupTimezone = (contentGroup: DSContentGroup<any>, timezone: string) => {
  contentGroup.contentDetials.forEach((member) => {
    if (member.startDate) member.startDate = convertFromUTcToTimezone(member.startDate, timezone);
    if (member.endDate) member.endDate = convertFromUTcToTimezone(member.endDate, timezone);
  });
  return contentGroup;
};

const convertDetailAndTimeTimezone = (detailDateAndTimes: DSDetailAndTime<any>[], timezone: string) => {
  detailDateAndTimes.forEach((d) => {
    if (d.startTime) d.startTime = convertFromUTcToTimezone(d.startTime, timezone);
    if (d.endTime) d.endTime = convertFromUTcToTimezone(d.endTime, timezone);
  });
  return detailDateAndTimes;
};

/**
 * Convert all times in the player to the player's timezone
 * @param player
 * @returns
 */
export const convertPlayerAllTimesToPlayerTimezone = (player: DSPlayerOBj<string>): DSPlayerOBj<Date> => {
  const playerTimezone = player.playerTimeZone;
  const np: DSPlayerOBj<Date> = JSON.parse(JSON.stringify(player));

  if (np.playerContent) convertContentTimezone(np.playerContent, playerTimezone);
  if (np.playerDefaultContent) convertContentTimezone(np.playerDefaultContent, playerTimezone);
  if (np.playerContentGroup) convertContentGroupTimezone(np.playerContentGroup, playerTimezone);
  if (np.areaDefaultContent) convertContentTimezone(np.areaDefaultContent, playerTimezone);

  for (const area of np.areaContentList || []) {
    if (area.contentGroup) convertContentGroupTimezone(area.contentGroup, playerTimezone);
    if (area.content) convertContentTimezone(area.content, playerTimezone);
  }

  for (const campaign of np.playerCampaigns || []) {
    if (campaign.defaultContent) convertContentTimezone(campaign.defaultContent, playerTimezone);
    for (const detail of campaign.details || []) {
      if (detail.periodStart) detail.periodStart = convertFromUTcToTimezone(detail.periodStart, playerTimezone);
      if (detail.periodEnd) detail.periodEnd = convertFromUTcToTimezone(detail.periodEnd, playerTimezone);
      detail.detailDateAndTimes = convertDetailAndTimeTimezone(detail.detailDateAndTimes, playerTimezone);
      detail.detailContents.forEach((content) => {
        if (content.contentGroup) convertContentGroupTimezone(content.contentGroup, playerTimezone);
        if (content.content) convertContentTimezone(content.content, playerTimezone);
      });
    }
  }

  if (np.playerChannel) {
    if (np.playerChannel.defaultContent) convertContentTimezone(np.playerChannel.defaultContent, playerTimezone);

    for (const detail of np.playerChannel.details || []) {
      if (detail.periodStart) detail.periodStart = convertFromUTcToTimezone(detail.periodStart, playerTimezone);
      if (detail.periodEnd) detail.periodEnd = convertFromUTcToTimezone(detail.periodEnd, playerTimezone);
      detail.detailDateAndTimes = convertDetailAndTimeTimezone(detail.detailDateAndTimes, playerTimezone);
      detail.detailContents.forEach((content) => {
        if (content.contentGroup) convertContentGroupTimezone(content.contentGroup, playerTimezone);
        if (content.content) convertContentTimezone(content.content, playerTimezone);
      });
    }
  }

  return np;
};
